import React, { useEffect, useState } from 'react';
import { ChevronDown, Clock3, MapPin } from 'lucide-react';
import api from '../api/client';
import RaceResultsViewer from './RaceResultsViewer';
import './ChampionshipTabs.css';

interface ResultSessionSummary {
  sessionId: number;
  filename: string;
  sessionType: string;
  sessionName: string;
  trackName: string;
  trackLayoutName: string;
  isCompleted: boolean;
  sessionDate: string;
  serverId: string;
}

interface ResultRoundSummary {
  roundId: string;
  raceId: string;
  track: string;
  layout: string;
  date: string;
  startTime: string;
  sessions: ResultSessionSummary[];
}

interface ChampionshipResultsTabProps {
  championshipId: string;
}

const ChampionshipResultsTab: React.FC<ChampionshipResultsTabProps> = ({ championshipId }) => {
  const [rounds, setRounds] = useState<ResultRoundSummary[]>([]);
  const [viewingSessionId, setViewingSessionId] = useState<number | null>(null);
  const [expandedRounds, setExpandedRounds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (championshipId) {
      loadRounds();
    }
  }, [championshipId]);

  const loadRounds = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getResultsChampionshipRounds(championshipId);
      setRounds(res.data.rounds || []);
    } catch (err) {
      console.error('Failed to load championship results rounds:', err);
      setError('Unable to load championship rounds.');
    } finally {
      setLoading(false);
    }
  };

  const toggleRound = (roundId: string) => {
    setExpandedRounds((prev) => {
      const next = new Set(prev);
      if (next.has(roundId)) {
        next.delete(roundId);
      } else {
        next.add(roundId);
      }
      return next;
    });
  };

  const handleViewSession = (session: ResultSessionSummary) => {
    setViewingSessionId(session.sessionId);
  };

  const handleDeleteSession = async (session: ResultSessionSummary) => {
    const confirmed = window.confirm(
      `Delete imported result for ${session.sessionType} ${session.sessionName}? This will remove the session and all related rows from the database.`
    );
    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);
      await api.deleteResultSession(session.sessionId.toString());
      if (viewingSessionId === session.sessionId) {
        setViewingSessionId(null);
      }
      await loadRounds();
    } catch (err) {
      console.error('Failed to delete session:', err);
      setError('Unable to delete result session.');
    } finally {
      setLoading(false);
    }
  };

  const importedRoundsCount = rounds.filter((round) => round.sessions.length > 0).length;

  return (
    <div className="championship-tab-content championship-results-tab">
      <div className="results-summary">
        <h3>Championship Results</h3>
        <p>{importedRoundsCount} rounds with imported sessions</p>
      </div>

      {loading && <div className="loading">Loading championship results...</div>}
      {error && <div className="error-message">{error}</div>}

      {!loading && !error && rounds.length === 0 && (
        <div className="empty-state">
          <p>No result sessions imported yet for this championship.</p>
          <p>
            Results will appear here automatically after races are completed and imported from the results directory.
          </p>
        </div>
      )}

      <div className="rounds-list">
        {rounds.map((round) => (
          <div key={round.roundId} className="round-card">
            <button className="round-header" onClick={() => toggleRound(round.roundId)}>
              <div>
                <div className="round-title">
                  {round.track} / {round.layout}
                </div>
                <div className="round-subtitle">
                  {round.date} · {round.startTime}
                </div>
              </div>
              <ChevronDown size={20} className={expandedRounds.has(round.roundId) ? 'rotated' : ''} />
            </button>

            {expandedRounds.has(round.roundId) && (
              <div className="round-details">
                {round.sessions.length === 0 ? (
                  <div className="empty-state">No sessions found for this round yet.</div>
                ) : (
                  <div className="session-grid">
                    {round.sessions.map((session) => (
                      <div key={session.sessionId} className="session-card">
                        <div className="session-type">{session.sessionType.toUpperCase()}</div>
                        <div className="session-name">{session.sessionName}</div>
                        <div className="session-info">
                          <MapPin size={14} />
                          <span>
                            {session.trackName} {session.trackLayoutName}
                          </span>
                        </div>
                        <div className="session-info">
                          <Clock3 size={14} />
                          <span>
                            {session.sessionDate ? new Date(session.sessionDate).toLocaleString() : 'No timestamp'}
                          </span>
                        </div>
                        <div className="session-actions">
                          <button className="view-session-button" onClick={() => handleViewSession(session)}>
                            View {session.sessionType} results
                          </button>
                          <button className="delete-session-button" onClick={() => handleDeleteSession(session)}>
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {viewingSessionId !== null && (
        <RaceResultsViewer
          sessionId={viewingSessionId}
          onClose={() => setViewingSessionId(null)}
          onDeleteSuccess={() => loadRounds()}
          fetchResult={async () => api.getResultsSession(viewingSessionId.toString())}
        />
      )}
    </div>
  );
};

export default ChampionshipResultsTab;
